import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

// Componente Header Reutilizable
import PageHeader from '../../cpanel/PageHeader';

// Componentes Comunes
import BuscadorInteligente from '../../ui/BuscadorInteligente';
import CardPolizaAccion from '../../ui/CardPolizaAccion';

// Componente de Formulario Unificado
import FormularioSiniestro from '../../Formularios/FormularioSiniestro';

const CargaSiniestroPage = () => {
    const navigate = useNavigate();

    // --- ESTADOS ---
    const [step, setStep] = useState(1); // 1: Buscar, 2: Cargar, 3: Éxito
    
    // Resultados del Buscador
    const [results, setResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);

    // Selección
    const [polizaFound, setPolizaFound] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [loadingSelection, setLoadingSelection] = useState(false);

    // Éxito
    const [successData, setSuccessData] = useState(null);

    // --- HANDLERS ---
    const handleSearchResults = (data) => {
        setSearchPerformed(true);
        setResults(data);
    };

    const handleSelectPoliza = async (poliza) => {
        setLoadingSelection(true);
        try {
            if (poliza.clienteId) {
                const clientRef = doc(db, 'public/data/clientes', poliza.clienteId);
                const clientSnap = await getDoc(clientRef);
                
                if (clientSnap.exists()) {
                    setClientData({ id: clientSnap.id, ...clientSnap.data() });
                    setPolizaFound(poliza);
                    setStep(2);
                } else {
                    alert("No se encontraron los datos del cliente asociado.");
                }
            } else {
                alert("La póliza no tiene un cliente asociado válido.");
            }
        } catch (error) {
            console.error("Error al seleccionar:", error);
            alert("Error al cargar datos del cliente.");
        } finally {
            setLoadingSelection(false);
        }
    };

    const handleCancel = () => {
        setStep(1);
        setPolizaFound(null);
        setClientData(null);
        setResults([]);
        setSearchPerformed(false);
        setSuccessData(null);
    };

    const handleSuccess = (msg) => {
        setSuccessData({
            id: polizaFound.id,
            nPoliza: polizaFound.nPoliza
        });
        setStep(3);
    };

    const handleNewCarga = () => {
        handleCancel();
    };

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased flex flex-col">
            
            {/* ENCABEZADO REUTILIZABLE (Oculto en pantalla de éxito) */}
            {step !== 3 && (
                <PageHeader 
                    title="Carga y Denuncia de Siniestros"
                    subtitle={
                        step === 1 
                            ? "Paso 1: Localiza la póliza afectada para iniciar el reclamo" 
                            : `Registrando denuncia para la Póliza #${polizaFound?.nPoliza}`
                    }
                    topBorderGradient="from-rose-600 via-rose-500 to-amber-500"
                    buttons={step === 2 ? [
                        { label: 'Cambiar Póliza', onClick: handleCancel },
                        { label: 'Cancelar', onClick: () => navigate('/cpanel') }
                    ] : []}
                />
            )}

            {/* CONTENIDO PRINCIPAL */}
            <div className="w-full px-8 pt-8 max-w-[1400px] mx-auto space-y-6 flex-grow">
                
                {/* PASO 1: BÚSQUEDA Y SELECCIÓN */}
                {step === 1 && (
                    <div className="space-y-8 animate-fade-in">
                        
                        {/* CABECERA CENTRADA Y ARMONIOSA DE BÚSQUEDA */}
                        <div className="flex flex-col items-center text-center space-y-3 border-b border-slate-200/80 pb-6 max-w-3xl mx-auto">
                            <span className="bg-rose-50 text-rose-700 text-[10px] font-black px-3.5 py-1 rounded-full border border-rose-200 uppercase tracking-wider">
                                Denuncia de Siniestro
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                                Buscar Póliza Afectada
                            </h2>
                            <p className="text-xs sm:text-sm text-slate-400 font-medium max-w-xl">
                                Ingresa el número de póliza o la patente del vehículo para vincular el reclamo
                            </p>
                        </div>

                        {/* BUSCADOR DENTRO DE TARJETA FLOTANTE */}
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200/70 max-w-3xl mx-auto text-left">
                            <BuscadorInteligente 
                                scope="polizas" 
                                onResult={handleSearchResults}
                                onLoading={setLoadingSearch}
                                placeholder="Buscar por N° Póliza o Patente..."
                                autoFocus={true}
                            />
                        </div>

                        {/* OVERLAY DE CARGA AL SELECCIONAR PÓLIZA */}
                        {loadingSelection && (
                            <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-xs z-50 flex items-center justify-center">
                                <div className="bg-white p-6 rounded-2xl shadow-xl flex items-center gap-4 border border-slate-200/80">
                                    <div className="w-7 h-7 border-3 border-rose-600 border-t-transparent rounded-full animate-spin" />
                                    <span className="font-bold text-slate-700 text-xs uppercase tracking-wider">Cargando datos del asegurado...</span>
                                </div>
                            </div>
                        )}

                        {/* RESULTADOS DE BÚSQUEDA */}
                        {!loadingSearch && searchPerformed && (
                            <div className="space-y-4 max-w-5xl mx-auto">
                                <div className="flex items-center justify-between border-b border-slate-200/60 pb-3">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider">
                                        Resultados Encontrados ({results.length})
                                    </h3>
                                </div>

                                {results.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {results.map(poliza => (
                                            <CardPolizaAccion 
                                                key={poliza.id}
                                                poliza={poliza}
                                                cliente={{ 
                                                    nombre: poliza.clienteNombre || 'Cliente', 
                                                    apellido: poliza.clienteApellido || '', 
                                                    dni: '', 
                                                    cuit: '' 
                                                }}
                                                onAction={() => handleSelectPoliza(poliza)}
                                                actionLabel="Seleccionar para Siniestro"
                                                colorTheme="red"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-white rounded-3xl border border-slate-200/70 shadow-2xs">
                                        <div className="mx-auto w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                                            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <p className="text-slate-500 font-bold text-sm">No se encontraron pólizas con ese criterio.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* PASO 2: FORMULARIO DE SINIESTRO */}
                {step === 2 && polizaFound && clientData && (
                    <div className="animate-fade-in text-left space-y-6">
                        
                        {/* BREADCRUMB & NAVEGACIÓN */}
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
                            <button onClick={() => setStep(1)} className="hover:text-rose-600 transition-colors">
                                Volver a Resultados
                            </button>
                            <span>/</span>
                            <span className="font-bold text-slate-800 uppercase">Alta de Siniestro</span>
                        </div>
                        
                        {/* CONTENEDOR DEL FORMULARIO CON HEADER INTERNO */}
                        <div className="bg-white rounded-3xl shadow-sm border border-slate-200/70 overflow-hidden">
                            <div className="bg-rose-50/70 px-8 py-5 border-b border-rose-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h3 className="font-black text-rose-950 text-lg flex items-center gap-2 uppercase">
                                        {clientData.nombre} {clientData.apellido}
                                    </h3>
                                    <div className="text-xs text-rose-800/80 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
                                        <span className="font-mono font-bold bg-white px-2.5 py-0.5 rounded-md border border-rose-200 text-rose-900">
                                            Póliza #{polizaFound.nPoliza}
                                        </span>
                                        <span>&bull;</span>
                                        <span className="uppercase font-bold text-rose-900">{polizaFound.compania}</span>
                                    </div>
                                </div>
                                
                                <button 
                                    onClick={handleCancel} 
                                    className="px-4 py-2 rounded-xl border border-rose-200/90 text-rose-700 font-bold hover:bg-white transition-all text-xs bg-white shadow-2xs cursor-pointer"
                                >
                                    Cambiar Póliza
                                </button>
                            </div>

                            <div className="p-8">
                                <FormularioSiniestro 
                                    poliza={polizaFound}
                                    clientData={clientData}
                                    onCancel={handleCancel}
                                    onSuccess={handleSuccess}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* PASO 3: PANTALLA DE ÉXITO */}
                {step === 3 && successData && (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center animate-fade-in my-auto flex-grow max-w-2xl mx-auto">
                        <div className="w-20 h-20 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-3xl flex items-center justify-center mb-6 shadow-2xs">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        <h2 className="text-2xl font-black text-slate-900 mb-2">¡Siniestro Registrado con Éxito!</h2>
                        <p className="text-slate-500 text-sm mb-1">
                            La denuncia para la póliza <span className="font-mono font-black text-rose-600">#{successData.nPoliza}</span> se guardó correctamente.
                        </p>
                        <p className="text-slate-400 text-xs mb-8 font-medium">
                            Asegurado: <strong className="text-slate-700 uppercase">{clientData?.nombre} {clientData?.apellido}</strong>
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md justify-center">
                            <button 
                                onClick={() => navigate(`/clientes/${clientData.id}/polizas/${successData.id}`)} 
                                className="flex-1 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold shadow-2xs hover:bg-slate-800 transition text-xs uppercase tracking-wider cursor-pointer"
                            >
                                Ver Detalle
                            </button>

                            <button 
                                onClick={handleNewCarga}
                                className="flex-1 bg-rose-600 text-white px-5 py-3 rounded-xl font-bold shadow-2xs hover:bg-rose-700 transition text-xs uppercase tracking-wider cursor-pointer"
                            >
                                Cargar Otro
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none shrink-0">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default CargaSiniestroPage;